import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconExternal, Logo } from '../../assets';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './index.scss';

const CaseStudyLayout = (props) => {
    const {
        title,
        link,
        subtitle,
        heroImage,
        duration,
        tools,
        team,
        role,
        summary,
        sections = []
    } = props;

    const navigate = useNavigate();

    const [activeId, setActiveId] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);

    const slides = Array.isArray(heroImage) ? heroImage : [heroImage];

    useEffect(() => {
        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveId(entry.target.id);
                }
            });
        };

        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0,
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        sections.forEach((sec) => {
            const el = document.getElementById(sec.id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [sections]);

    const SLIDE_INTERVAL = 5000;

    useEffect(() => {
        if (slides.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
        }, SLIDE_INTERVAL);

        return () => clearInterval(interval);
    }, [slides.length, currentIndex]);

    useEffect(() => {
        window.scrollTo(0, 0);
        AOS.init({
            duration: 1200,
            once: true
        });
    }, []);

    return (
        <>
            <div className='case_study_bg' aria-hidden='true'>
                <Logo />
            </div>
            <div className='bottom_viewport_fade' aria-hidden='true' />

            {/* Main Page Content Wrapper */}
            <div className='case_study_layout'>
                {/* Top Navigation */}
                <div className='case_study_nav'>
                    <div
                        className='case_study_nav_back'
                        onClick={() => navigate(-1)}
                    >
                        <div className='case_study_nav_back_arrow'></div>
                        <span>back to home</span>
                    </div>
                </div>

                {/* Case Study Intro Header */}
                <div className='case_study_summary'>
                    <div className='case_study_summary_details'>
                        <div className='case_study_summary_details_header'>
                            <span className='case_study_summary_details_header_title'>
                                {title}
                                {link && <a
                                    href={link}
                                    className='external_link'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    <IconExternal className='externalIcon' />
                                </a>}
                            </span>
                            {subtitle && (<span className='case_study_summary_details_header_subtitle'>{subtitle}</span>)}
                        </div>
                        <br></br>
                        <div className='case_study_summary_details_text'>
                            {summary}
                        </div>
                        <br></br>
                        <div className='case_study_summary_details_list'>
                            {duration && (
                                <div className='detail_row'>
                                    <span className='detail_label'>Duration</span>
                                    <span className='detail_value'>{duration}</span>
                                </div>
                            )}
                            {tools && (
                                <div className='detail_row'>
                                    <span className='detail_label'>Software & Tools</span>
                                    <span className='detail_value'>{tools}</span>
                                </div>
                            )}
                            {team && (
                                <div className='detail_row'>
                                    <span className='detail_label'>Team</span>
                                    <span className='detail_value'>{team}</span>
                                </div>
                            )}
                            {role && (
                                <div className='detail_row'>
                                    <span className='detail_label'>Role</span>
                                    <span className='detail_value'>{role}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='case_study_summary_slideshow'>
                        <div className='slideshow_wrapper'>
                            {slides.map((imgSrc, idx) => (
                                <img
                                    key={idx}
                                    src={imgSrc}
                                    className={`case_study_summary_img ${idx === currentIndex ? 'active' : ''}`}
                                    alt={`${title} slide ${idx + 1}`}
                                />
                            ))}
                        </div>
                        {slides.length > 1 && (
                            <div className='slideshow_markers'>
                                {slides.map((_, index) => {
                                    const isActive = currentIndex === index;
                                    return (
                                        <div
                                            key={index}
                                            className={`slideshow_marker_track ${isActive ? 'active' : ''}`}
                                            onClick={() => setCurrentIndex(index)}
                                        >
                                            {isActive && (
                                                <div
                                                    key={currentIndex}
                                                    className='slideshow_marker_fill'
                                                    style={{ animationDuration: `${SLIDE_INTERVAL}ms` }}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <div className='case_study'>
                    <div className='case_study_links'>
                        {sections.map((sec) => {
                            const isActive = activeId === sec.id;
                            return (
                                <button
                                    key={sec.id}
                                    className={`case_study_link ${isActive ? 'active' : ''}`}
                                    data-text={sec.title}
                                    onClick={() => {
                                        setActiveId(sec.id);
                                        const el = document.getElementById(sec.id);
                                        if (el) {
                                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                    }}
                                >
                                    {`${isActive ? '' : '❖ '}${sec.title}`}
                                </button>
                            );
                        })}
                    </div>
                    <div className='case_study_section_container'>
                        {sections.map((sec) => (
                            <div key={sec.id} id={sec.id} className='case_study_section'>
                                <h2 className='case_study_section_header'>
                                    {sec.title}
                                </h2>
                                <div className='case_study_section_description fade_up_animation'>
                                    {sec.content}
                                </div>
                                <br></br>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CaseStudyLayout;